import { useMemo } from "react"
import styled, { createGlobalStyle } from "styled-components"
import { format, isAfter, isBefore, differenceInCalendarDays } from "date-fns"
import { ko } from "date-fns/locale"
import { Printer, FileDown } from "lucide-react"

const STATUS = {
  done: { key: "done", label: "완료", color: "#16a34a", bg: "#dcfce7" },
  delayed: { key: "delayed", label: "지연", color: "#ef4444", bg: "#fee2e2" },
  progress: { key: "progress", label: "진행중", color: "#2563eb", bg: "#dbeafe" },
  planned: { key: "planned", label: "예정", color: "#9ca3af", bg: "#f3f4f6" },
}

const normalizeDate = (value) => {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const computeBaselineProgress = (startDate, endDate, refDate) => {
  if (!startDate || !endDate) return 0
  const start = new Date(startDate).getTime()
  const end = new Date(endDate).getTime()
  const ref = refDate ? new Date(refDate).getTime() : Date.now()
  if (isNaN(start) || isNaN(end) || end <= start) return 0
  if (ref <= start) return 0
  if (ref >= end) return 100
  return Math.round(((ref - start) / (end - start)) * 100)
}

const getStatus = (task, refDate) => {
  const progress = Number(task.progress || 0)
  const ref = refDate ? new Date(refDate) : new Date()
  if (progress >= 100) return STATUS.done
  if (task.startDate && isAfter(new Date(task.startDate), ref)) return STATUS.planned
  const baseline = computeBaselineProgress(task.startDate, task.endDate, ref)
  if (task.endDate && isBefore(new Date(task.endDate), ref) && progress < 100) return STATUS.delayed
  if (progress < baseline - 5) return STATUS.delayed
  return STATUS.progress
}

const PrintStyle = createGlobalStyle`
  @media print {
    @page { size: A4; margin: 14mm; }
    body * { visibility: hidden; }
    #report-print-area, #report-print-area * { visibility: visible; }
    #report-print-area {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      background: white;
    }
    .no-print { display: none !important; }
  }
`

const Page = styled.div`
  height: 100%;
  overflow-y: auto;
  background: #f1f5f9;
  padding: 24px;
`

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  max-width: 900px;
  margin: 0 auto 16px auto;
`

const ToolBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  font-size: 13px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  color: #111827;
  &:hover { background: #f9fafb; }
`

const Paper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  background: white;
  padding: 36px 44px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.04);
  color: #0f172a;
  font-size: 13px;
  line-height: 1.55;
`

const ReportHeader = styled.div`
  border-bottom: 2px solid #0f172a;
  padding-bottom: 12px;
  margin-bottom: 20px;
`

const ReportTitle = styled.h1`
  font-size: 22px;
  margin: 0 0 6px 0;
  color: #0f172a;
`

const HeaderMeta = styled.div`
  display: flex;
  justify-content: space-between;
  color: #475569;
  font-size: 12px;
`

const SectionTitle = styled.h2`
  font-size: 15px;
  color: #1e293b;
  margin: 24px 0 10px 0;
  padding-left: 8px;
  border-left: 3px solid #2563eb;
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 8px;
`

const StatBox = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 10px 12px;
  background: #f8fafc;
`

const StatBoxLabel = styled.div`
  font-size: 11px;
  color: #64748b;
`

const StatBoxValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${(props) => props.color || "#0f172a"};
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
`

const Th = styled.th`
  text-align: left;
  border-bottom: 1px solid #cbd5e1;
  padding: 8px 6px;
  font-weight: 600;
  background: #f1f5f9;
  color: #334155;
`

const Td = styled.td`
  padding: 7px 6px;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: top;
`

const StatusPill = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  color: ${(props) => props.color};
  background: ${(props) => props.bg};
`

const ProgressBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const MiniBar = styled.div`
  position: relative;
  flex: 1;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
`

const MiniFill = styled.div`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${(props) => `${props.value}%`};
  background: ${(props) => props.color || "#2563eb"};
`

const csvEscape = (v) => {
  const s = v == null ? "" : String(v)
  if (s.includes(",") || s.includes("\"") || s.includes("\n")) {
    return '"' + s.replace(/"/g, '""') + '"'
  }
  return s
}

export default function ReportView({ tasks = [], users = [], projectName = "프로젝트", referenceDate }) {
  const refDate = referenceDate ? new Date(referenceDate) : new Date()

  const getUserName = (assignee) => {
    const u = users.find((x) => x.id === assignee)
    return u?.name || assignee || "-"
  }

  const projectRange = useMemo(() => {
    const ds = tasks.flatMap((t) => [normalizeDate(t.startDate), normalizeDate(t.endDate)]).filter(Boolean)
    if (!ds.length) return null
    return {
      start: new Date(Math.min(...ds.map((d) => d.getTime()))),
      end: new Date(Math.max(...ds.map((d) => d.getTime()))),
    }
  }, [tasks])

  const summary = useMemo(() => {
    const list = tasks
    if (!list.length) return { total: 0, avgActual: 0, avgBaseline: 0, done: 0, delayed: 0, progress: 0, planned: 0 }
    let actualSum = 0, baselineSum = 0, done = 0, delayed = 0, prog = 0, planned = 0
    list.forEach((t) => {
      const p = Number(t.progress || 0)
      const b = computeBaselineProgress(t.startDate, t.endDate, refDate)
      actualSum += p
      baselineSum += b
      const s = getStatus(t, refDate)
      if (s === STATUS.done) done++
      else if (s === STATUS.delayed) delayed++
      else if (s === STATUS.planned) planned++
      else prog++
    })
    return {
      total: list.length,
      avgActual: Math.round(actualSum / list.length),
      avgBaseline: Math.round(baselineSum / list.length),
      done, delayed, progress: prog, planned,
    }
  }, [tasks, refDate])

  const delayedList = useMemo(() => {
    return tasks
      .map((t) => ({ t, s: getStatus(t, refDate) }))
      .filter(({ s }) => s === STATUS.delayed)
      .map(({ t }) => t)
  }, [tasks, refDate])

  const handlePrint = () => {
    window.print()
  }

  const handleExportCsv = () => {
    const header = ["코드", "작업명", "담당자", "시작일", "종료일", "기간(일)", "실제(%)", "목표(%)", "상태"]
    const rows = tasks.map((t, i) => {
      const start = normalizeDate(t.startDate)
      const end = normalizeDate(t.endDate)
      const days = start && end ? differenceInCalendarDays(end, start) + 1 : ""
      const s = getStatus(t, refDate)
      const b = computeBaselineProgress(t.startDate, t.endDate, refDate)
      return [
        i + 1,
        t.name || "",
        getUserName(t.assignee),
        start ? format(start, "yyyy-MM-dd") : "",
        end ? format(end, "yyyy-MM-dd") : "",
        days,
        Number(t.progress || 0),
        b,
        s.label,
      ]
    })
    const csv = [header, ...rows].map((r) => r.map(csvEscape).join(",")).join("\n")
    const bom = "﻿"
    const blob = new Blob([bom + csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${projectName}_보고서_${format(refDate, "yyyyMMdd")}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Page>
      <PrintStyle />
      <Toolbar className="no-print">
        <ToolBtn onClick={handleExportCsv}>
          <FileDown size={14} /> CSV/Excel
        </ToolBtn>
        <ToolBtn onClick={handlePrint}>
          <Printer size={14} /> PDF / 인쇄
        </ToolBtn>
      </Toolbar>

      <Paper id="report-print-area">
        <ReportHeader>
          <ReportTitle>{projectName} 프로젝트 보고서</ReportTitle>
          <HeaderMeta>
            <div>
              기간:{" "}
              {projectRange
                ? `${format(projectRange.start, "yyyy.MM.dd")} ~ ${format(projectRange.end, "yyyy.MM.dd")}`
                : "-"}
            </div>
            <div>기준일: {format(refDate, "yyyy년 M월 d일 (EEEE)", { locale: ko })}</div>
          </HeaderMeta>
        </ReportHeader>

        <SectionTitle>요약</SectionTitle>
        <StatsGrid>
          <StatBox>
            <StatBoxLabel>전체 작업</StatBoxLabel>
            <StatBoxValue>{summary.total}</StatBoxValue>
          </StatBox>
          <StatBox>
            <StatBoxLabel>실제 평균</StatBoxLabel>
            <StatBoxValue color="#2563eb">{summary.avgActual}%</StatBoxValue>
          </StatBox>
          <StatBox>
            <StatBoxLabel>목표 평균</StatBoxLabel>
            <StatBoxValue color="#64748b">{summary.avgBaseline}%</StatBoxValue>
          </StatBox>
          <StatBox>
            <StatBoxLabel>달성도</StatBoxLabel>
            <StatBoxValue color={summary.avgActual >= summary.avgBaseline ? "#16a34a" : "#ef4444"}>
              {summary.avgBaseline ? Math.round((summary.avgActual / summary.avgBaseline) * 100) : 100}%
            </StatBoxValue>
          </StatBox>
          <StatBox>
            <StatBoxLabel>완료</StatBoxLabel>
            <StatBoxValue color={STATUS.done.color}>{summary.done}</StatBoxValue>
          </StatBox>
          <StatBox>
            <StatBoxLabel>진행중</StatBoxLabel>
            <StatBoxValue color={STATUS.progress.color}>{summary.progress}</StatBoxValue>
          </StatBox>
          <StatBox>
            <StatBoxLabel>지연</StatBoxLabel>
            <StatBoxValue color={STATUS.delayed.color}>{summary.delayed}</StatBoxValue>
          </StatBox>
          <StatBox>
            <StatBoxLabel>예정</StatBoxLabel>
            <StatBoxValue color={STATUS.planned.color}>{summary.planned}</StatBoxValue>
          </StatBox>
        </StatsGrid>

        {delayedList.length > 0 && (
          <>
            <SectionTitle>지연 작업 ({delayedList.length})</SectionTitle>
            <Table>
              <thead>
                <tr>
                  <Th style={{ width: 40 }}>#</Th>
                  <Th>작업명</Th>
                  <Th style={{ width: 80 }}>담당자</Th>
                  <Th style={{ width: 100 }}>종료일</Th>
                  <Th style={{ width: 60 }}>실제</Th>
                  <Th style={{ width: 60 }}>목표</Th>
                </tr>
              </thead>
              <tbody>
                {delayedList.map((t, i) => {
                  const end = normalizeDate(t.endDate)
                  const b = computeBaselineProgress(t.startDate, t.endDate, refDate)
                  return (
                    <tr key={t.id || i}>
                      <Td>{i + 1}</Td>
                      <Td>{t.name}</Td>
                      <Td>{getUserName(t.assignee)}</Td>
                      <Td>{end ? format(end, "yyyy-MM-dd") : "-"}</Td>
                      <Td>{Number(t.progress || 0)}%</Td>
                      <Td>{b}%</Td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </>
        )}

        <SectionTitle>작업 목록</SectionTitle>
        <Table>
          <thead>
            <tr>
              <Th style={{ width: 36 }}>#</Th>
              <Th>작업명</Th>
              <Th style={{ width: 76 }}>담당자</Th>
              <Th style={{ width: 92 }}>시작</Th>
              <Th style={{ width: 92 }}>종료</Th>
              <Th style={{ width: 130 }}>진척률</Th>
              <Th style={{ width: 64 }}>상태</Th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((t, i) => {
              const start = normalizeDate(t.startDate)
              const end = normalizeDate(t.endDate)
              const s = getStatus(t, refDate)
              const actual = Number(t.progress || 0)
              const baseline = computeBaselineProgress(t.startDate, t.endDate, refDate)
              return (
                <tr key={t.id || i}>
                  <Td>{i + 1}</Td>
                  <Td>{t.name}</Td>
                  <Td>{getUserName(t.assignee)}</Td>
                  <Td>{start ? format(start, "yyyy-MM-dd") : "-"}</Td>
                  <Td>{end ? format(end, "yyyy-MM-dd") : "-"}</Td>
                  <Td>
                    <ProgressBox>
                      <MiniBar>
                        <MiniFill value={baseline} color="#cbd5e1" />
                        <MiniFill value={actual} color={actual >= baseline ? "#16a34a" : "#ef4444"} />
                      </MiniBar>
                      <span style={{ minWidth: 56, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {actual}% / {baseline}%
                      </span>
                    </ProgressBox>
                  </Td>
                  <Td>
                    <StatusPill color={s.color} bg={s.bg}>
                      {s.label}
                    </StatusPill>
                  </Td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </Paper>
    </Page>
  )
}
